import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, apiBlob } from "../lib/api";
import { Badge, Button, Card, EmptyState, Modal, PageHeader, ProgressBar, Select, Spinner, statusTone, Table, Tabs } from "../components/ui";
import { PLACEMENT_LABELS, PROVIDER_LABELS, STATUS_LABELS, cls, fmtDateTime, fmtPct } from "../lib/format";

type Bundle = {
  test: { id: string; name: string; type: string; createdAt: string; runNumber: number; runId: string; status: string };
  summary: {
    total: number; inbox: number; category: number; spam: number; missing: number;
    inboxPct: number; categoryPct: number; spamPct: number; missingPct: number;
    inboxScore: number; deliverabilityScore: number; status: string;
  };
  providerBreakdown: { senderEmail: string; provider: string; total: number; inboxPct: number; categoryPct: number; spamPct: number; missingPct: number }[];
  history: { current: any; previous: any; avg7: { inboxPct: number | null; spamPct: number | null; score: number | null }; avg30: { inboxPct: number | null; spamPct: number | null; score: number | null }; deltas: { inbox: string | null; spam: string | null; score: string | null }; history: { date: string; summary: any }[] };
  spam: { score: number; threshold: number; status: string; source: string; rules: { rule: string; score: number; description: string }[]; checkedAt: string } | null;
  auth: {
    mechanisms: { mechanism: string; status: string; domain: string | null; alignment: string | null; diagnostic: string[] }[];
    infrastructure: { sendingIp: string | null; reverseDns: string | null; helo: string | null; smtpProvider: string | null; tls: string | null; dkimDomains: string[]; envelopeFrom: string | null; receivedHops: unknown[]; ipReputation: string; domainReputation: string } | null;
  };
  blacklist: { id: string; domain: string; domainBlacklisted: boolean; domainCount: number; ip: string | null; ipBlacklisted: boolean; ipCount: number; rows: { list: string; status: string; response: string | null; checkedAt: string }[]; checkedAt: string }[];
  content: { id: string; scores: Record<string, { score: number; status: string; explanation: string; recommendations: string[] }>; details: any; analyzedAt: string }[];
  technical: { id: string; messageId: string | null; from: string | null; to: string | null; subject: string | null; date: string | null; returnPath: string | null; senderIp: string | null; rawHeaders: string | null; receivedChain: unknown[] }[];
  generatedAt: string;
};

type PlacementRow = {
  id: string; recipient: string; provider: string; senderEmail: string; deliveredTo: string; folder: string | null;
  smtpBlacklisted: boolean; spf: string; dkim: string; dmarc: string; badge: string; label: string; checkedAt: string | null;
};

const SECTIONS = [
  { id: "overall", label: "Overall Results" },
  { id: "insights", label: "Deliverability Insights" },
  { id: "spam", label: "Spam Filter Score" },
  { id: "content", label: "Content Insights" },
  { id: "auth", label: "Authentication" },
  { id: "blacklist", label: "Blacklist Report" },
  { id: "placement", label: "Email Placement" },
  { id: "technical", label: "Technical Details" },
];

export function TestReport() {
  const { testId, runId } = useParams<{ testId: string; runId: string }>();
  const [tab, setTab] = useState("results");
  const [activeSection, setActiveSection] = useState("overall");
  const [placementFilters, setPlacementFilters] = useState({ provider: "", placement: "", auth: "" });
  const [detail, setDetail] = useState<PlacementRow | null>(null);
  const [showSpamDetails, setShowSpamDetails] = useState(false);
  const [blacklistDetail, setBlacklistDetail] = useState<Bundle["blacklist"][number] | null>(null);

  const { data: bundle, isLoading } = useQuery({
    queryKey: ["bundle", runId],
    queryFn: () => api<Bundle>(`/reports/bundle/${runId}`),
  });
  const { data: placementData, isLoading: placementsLoading } = useQuery({
    queryKey: ["placements", runId, placementFilters],
    queryFn: () =>
      api<{ rows: PlacementRow[]; total: number }>(
        `/reports/placements/${runId}?pageSize=200${placementFilters.provider ? `&provider=${placementFilters.provider}` : ""}${placementFilters.placement ? `&placement=${placementFilters.placement}` : ""}${placementFilters.auth ? `&auth=${placementFilters.auth}` : ""}`,
      ),
    enabled: Boolean(bundle),
  });

  useEffect(() => {
    if (!bundle) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id.replace("report-", ""));
          }
        }
      },
      { rootMargin: "-35% 0px -60% 0px" },
    );
    for (const sec of SECTIONS) {
      const el = document.getElementById(`report-${sec.id}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [bundle]);

  const pieData = useMemo(() => {
    if (!bundle) return [];
    return [
      { name: "Inbox", value: bundle.summary.inbox, color: "#16a34a" },
      { name: "Category", value: bundle.summary.category, color: "#d97706" },
      { name: "Spam", value: bundle.summary.spam, color: "#dc2626" },
      { name: "Missing", value: bundle.summary.missing, color: "#94a3b8" },
    ].filter((d) => d.value > 0);
  }, [bundle]);

  if (isLoading) return <Spinner label="Building report…" />;
  if (!bundle) return <EmptyState title="Report unavailable" description="This run may not exist or results are still being collected." />;

  const s = bundle.summary;

  return (
    <div>
      <PageHeader
        title={bundle.test.name}
        subtitle={`Run #${bundle.test.runNumber} · ${bundle.test.type === "one_time" ? "One-time test" : "Automated test"} · ${fmtDateTime(bundle.test.createdAt)}`}
        actions={
          <>
            <Badge tone={statusTone(bundle.test.status)}>{STATUS_LABELS[bundle.test.status] ?? bundle.test.status}</Badge>
            <Link to="/tests"><Button variant="secondary">All Tests</Button></Link>
          </>
        }
      />

      <Tabs
        tabs={[
          { id: "results", label: "Results & Insights" },
          { id: "config", label: "Test Configuration" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === "config" ? (
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          <Card title="Configuration">
            <dl className="space-y-3 text-sm">
              <Row k="Test name" v={bundle.test.name} />
              <Row k="Test type" v={bundle.test.type === "one_time" ? "One-time" : "Automated"} />
              <Row k="Created" v={fmtDateTime(bundle.test.createdAt)} />
              <Row k="Run number" v={String(bundle.test.runNumber)} />
            </dl>
          </Card>
          <Card title="What this report measures">
            <ul className="space-y-2 text-sm text-ink-600">
              <li>• Placement detection from actual IMAP folder scans of seed mailboxes</li>
              <li>• SPF / DKIM / DMARC parsed from received headers (provider Authentication-Results first, direct DNS verification as fallback)</li>
              <li>• Spam score from SpamAssassin when configured, otherwise the built-in heuristic analyzer</li>
              <li>• DNS blacklist checks for the sending domain and IP</li>
              <li>• Missing messages are reported as Missing — never guessed as spam</li>
            </ul>
          </Card>
        </div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-[220px_1fr]">
          <aside>
            <nav className="rounded-xl border border-ink-200 bg-white p-2 shadow-card">
              {SECTIONS.map((sec) => (
                <a
                  key={sec.id}
                  href={`#report-${sec.id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(`report-${sec.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={cls("block w-full rounded-lg px-3 py-2 text-left text-sm font-medium", activeSection === sec.id ? "bg-brand-50 text-brand-700" : "text-ink-600 hover:bg-ink-50")}
                >
                  {sec.label}
                </a>
              ))}
            </nav>
          </aside>

          <div className="min-w-0 space-y-6">
            <section id="report-overall" className="scroll-mt-24">
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                  <SummaryCard label="Inbox" count={s.inbox} pct={s.inboxPct} tone="success" />
                  <SummaryCard label="Category" count={s.category} pct={s.categoryPct} tone="warning" />
                  <SummaryCard label="Spam" count={s.spam} pct={s.spamPct} tone="danger" />
                  <SummaryCard label="Missing" count={s.missing} pct={s.missingPct} tone="neutral" />
                </div>
                <div className="grid gap-4 lg:grid-cols-2">
                  <Card title="Overall Inbox Placement Score">
                    <div className="text-4xl font-bold text-brand-600">{s.inboxScore}<span className="text-lg text-ink-400"> / 100</span></div>
                    <div className="mt-3"><ProgressBar value={s.inboxScore} tone="brand" /></div>
                    <p className="mt-3 text-xs text-ink-500">{s.total} placements analyzed in this run.</p>
                  </Card>
                  <Card title="Overall Deliverability Score">
                    <div className="flex items-center gap-3">
                      <span className="text-4xl font-bold text-ink-900">{s.deliverabilityScore}<span className="text-lg text-ink-400"> / 100</span></span>
                      <Badge tone={statusTone(s.status)}>{STATUS_LABELS[s.status] ?? s.status}</Badge>
                    </div>
                    <div className="mt-3"><ProgressBar value={s.deliverabilityScore} tone={s.deliverabilityScore >= 85 ? "success" : s.deliverabilityScore >= 70 ? "warning" : "danger"} /></div>
                    <p className="mt-3 text-xs text-ink-500">Weighted by inbox rate, spam and missing penalties, authentication and blacklist state.</p>
                  </Card>
                </div>
                <Card title="Placement distribution">
                  <div className="flex flex-wrap items-center justify-center gap-8">
                    <ResponsiveContainer width={280} height={220}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                          {pieData.map((d) => <Cell key={d.name} fill={d.color} />)}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {pieData.map((d) => (
                        <div key={d.name} className="flex items-center gap-2 text-sm">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />
                          <span className="text-ink-600">{d.name}</span>
                          <span className="font-semibold text-ink-800">{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
            </section>

            <section id="report-insights" className="scroll-mt-24">
                <Card title="Provider-level breakdown" subtitle="Placement by sender email and recipient provider">
                  <Table headers={["Sender", "Recipient Provider", "Inbox", "Category", "Spam", "Missing", "Total"]}>
                    {bundle.providerBreakdown.map((row, i) => (
                      <tr key={i} className="table-row-hover">
                        <td className="px-4 py-3 text-ink-800">{row.senderEmail}</td>
                        <td className="px-4 py-3">{PROVIDER_LABELS[row.provider] ?? row.provider}</td>
                        <td className="px-4 py-3 font-medium text-emerald-600">{fmtPct(row.inboxPct)}</td>
                        <td className="px-4 py-3 text-amber-600">{fmtPct(row.categoryPct)}</td>
                        <td className="px-4 py-3 text-red-600">{fmtPct(row.spamPct)}</td>
                        <td className="px-4 py-3 text-ink-500">{fmtPct(row.missingPct)}</td>
                        <td className="px-4 py-3 tabular-nums">{row.total}</td>
                      </tr>
                    ))}
                  </Table>
                  {!bundle.providerBreakdown.length && <p className="py-6 text-center text-sm text-ink-400">No placements recorded yet.</p>}
                </Card>
                <Card title="Inbox Placement by Provider">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={bundle.providerBreakdown}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="provider" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip />
                      <Bar dataKey="inboxPct" name="Inbox %" fill="#16a34a" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="spamPct" name="Spam %" fill="#dc2626" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
                <Card title="Historical comparison">
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <CompareCard label="Current test" inbox={s.inboxPct} spam={s.spamPct} score={s.deliverabilityScore} highlight />
                    <CompareCard label="Previous test" inbox={bundle.history.previous?.inboxPct} spam={bundle.history.previous?.spamPct} score={bundle.history.previous?.deliverabilityScore} />
                    <CompareCard label="7-day average" inbox={bundle.history.avg7.inboxPct} spam={bundle.history.avg7.spamPct} score={bundle.history.avg7.score} />
                    <CompareCard label="30-day average" inbox={bundle.history.avg30.inboxPct} spam={bundle.history.avg30.spamPct} score={bundle.history.avg30.score} />
                  </div>
                  {bundle.history.deltas.inbox && (
                    <div className="mt-4 flex flex-wrap gap-4 text-xs">
                      <Delta label="Inbox vs previous" value={Number(bundle.history.deltas.inbox)} />
                      <Delta label="Spam vs previous" value={Number(bundle.history.deltas.spam)} invert />
                      <Delta label="Score vs previous" value={Number(bundle.history.deltas.score)} />
                    </div>
                  )}
                </Card>
            </section>

            <section id="report-spam" className="scroll-mt-24">
              <Card title="Spam Filter Score" subtitle={bundle.spam ? `Source: ${bundle.spam.source === "spamassassin" ? "SpamAssassin" : "Built-in heuristic analyzer"}` : "No spam score computed"}>
                {bundle.spam ? (
                  <>
                    <div className="flex items-center gap-6">
                      <div className="text-5xl font-bold text-ink-900">{bundle.spam.score}<span className="text-xl text-ink-400"> / {bundle.spam.threshold}</span></div>
                      <div>
                        <Badge tone={bundle.spam.status === "spam" ? "danger" : "success"}>{bundle.spam.status === "spam" ? "Considered spam" : "Not considered spam"}</Badge>
                        <p className="mt-2 text-xs text-ink-500">Checked {fmtDateTime(bundle.spam.checkedAt)}</p>
                      </div>
                    </div>
                    <div className="mt-6">
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-ink-800">Triggered rules</h4>
                        <Button variant="ghost" size="sm" onClick={() => setShowSpamDetails(true)}>Show Details</Button>
                      </div>
                      <Table headers={["Rule", "Score", "Description"]}>
                        {bundle.spam.rules.map((r) => (
                          <tr key={r.rule} className="table-row-hover">
                            <td className="px-4 py-2.5 font-mono text-xs text-ink-700">{r.rule}</td>
                            <td className={cls("px-4 py-2.5 font-mono text-xs tabular-nums", r.score > 0 ? "text-red-600" : "text-emerald-600")}>{r.score > 0 ? `+${r.score}` : r.score}</td>
                            <td className="px-4 py-2.5 text-xs text-ink-500">{r.description}</td>
                          </tr>
                        ))}
                        {!bundle.spam.rules.length && <tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-ink-400">No rules triggered.</td></tr>}
                      </Table>
                    </div>
                    <p className="mt-4 text-xs text-ink-400">Heuristic scores are content signals, not mailbox-provider verdicts. Actual placement is always read from the seed mailbox.</p>
                  </>
                ) : (
                  <p className="text-sm text-ink-500">Spam analysis runs after message scanning completes.</p>
                )}
              </Card>
            </section>

            <section id="report-content" className="scroll-mt-24">
              <Card title="Content Insights" subtitle="Heuristic analysis of the sent message copy">
                {bundle.content.length ? (
                  <div className="grid gap-4 lg:grid-cols-2">
                    {bundle.content[0] && (
                      <>
                        <ScoreCard title="Subject Line" score={bundle.content[0].scores.subjectLine} />
                        <ScoreCard title="Personalization" score={bundle.content[0].scores.personalization} />
                        <ScoreCard title="Content Quality" score={bundle.content[0].scores.contentQuality} />
                        <ScoreCard title="Call-to-Action" score={bundle.content[0].scores.cta} />
                        <ScoreCard title="Structure" score={bundle.content[0].scores.structure} />
                      </>
                    )}
                    <Card className="border-0 shadow-none" title="Links and images">
                      <div className="space-y-2 text-sm">
                        <ContentLine k="Total URLs" v={bundle.content[0]?.details?.links?.total ?? 0} />
                        <ContentLine k="Tracking URLs" v={bundle.content[0]?.details?.links?.tracking ?? 0} />
                        <ContentLine k="Redirects" v={bundle.content[0]?.details?.links?.redirects ?? 0} />
                        <ContentLine k="Suspicious domains" v={bundle.content[0]?.details?.links?.suspicious ?? 0} />
                        <ContentLine k="Image count" v={bundle.content[0]?.details?.images?.count ?? 0} />
                        <ContentLine k="Image-to-text ratio" v={bundle.content[0]?.details?.images?.textRatio ?? 0} />
                        <ContentLine k="Images missing alt" v={bundle.content[0]?.details?.images?.missingAlt ?? 0} />
                      </div>
                    </Card>
                    <Card className="border-0 shadow-none" title="Spam triggers found">
                      {bundle.content[0]?.details?.spamTriggers?.length ? (
                        <div className="flex flex-wrap gap-2">
                          {bundle.content[0].details.spamTriggers.map((t: any) => (
                            <Badge key={t.term} tone="warning">{t.term} × {t.count}</Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-ink-500">No common spam trigger terms detected.</p>
                      )}
                      <p className="mt-3 text-xs text-ink-400">These are content heuristics — they do not automatically cause spam placement.</p>
                    </Card>
                  </div>
                ) : (
                  <p className="text-sm text-ink-500">Content analysis runs after message scanning completes.</p>
                )}
              </Card>
            </section>

            <section id="report-auth" className="scroll-mt-24">
                <Card title="Authentication Results" subtitle="Parsed from received headers">
                  <Table headers={["Mechanism", "Status", "Domain", "Alignment", "Details"]}>
                    {bundle.auth.mechanisms.map((m) => (
                      <tr key={m.mechanism} className="table-row-hover">
                        <td className="px-4 py-3 font-semibold uppercase text-ink-700">{m.mechanism}</td>
                        <td className="px-4 py-3"><Badge tone={statusTone(m.status)}>{m.status.toUpperCase()}</Badge></td>
                        <td className="px-4 py-3 text-ink-600">{m.domain ?? "—"}</td>
                        <td className="px-4 py-3"><Badge tone={m.alignment === "pass" ? "success" : m.alignment === "fail" ? "danger" : "neutral"}>{m.alignment ?? "—"}</Badge></td>
                        <td className="max-w-md px-4 py-3 text-xs text-ink-500">{m.diagnostic[0] ?? "—"}</td>
                      </tr>
                    ))}
                  </Table>
                  {!bundle.auth.mechanisms.length && <p className="py-6 text-center text-sm text-ink-400">No authentication results captured.</p>}
                </Card>
                {bundle.auth.infrastructure && (
                  <Card title="Sending Infrastructure" subtitle="Extracted from the received chain">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      <Infra k="Sending IP" v={bundle.auth.infrastructure.sendingIp ?? "Unknown"} />
                      <Infra k="Reverse DNS" v={bundle.auth.infrastructure.reverseDns ?? "Not available"} />
                      <Infra k="HELO / EHLO" v={bundle.auth.infrastructure.helo ?? "Not available"} />
                      <Infra k="SMTP Provider" v={bundle.auth.infrastructure.smtpProvider ?? "Unknown"} />
                      <Infra k="TLS" v={bundle.auth.infrastructure.tls ?? "Not available"} />
                      <Infra k="Envelope From" v={bundle.auth.infrastructure.envelopeFrom ?? "Not available"} />
                      <Infra k="DKIM Signing Domains" v={bundle.auth.infrastructure.dkimDomains.join(", ") || "None"} />
                      <Infra k="IP Reputation" v={bundle.auth.infrastructure.ipReputation} />
                      <Infra k="Domain Reputation" v={bundle.auth.infrastructure.domainReputation} />
                    </div>
                    <p className="mt-4 text-xs text-ink-400">"Not available" means no public reputation source is configured — we never invent scores.</p>
                  </Card>
                )}
            </section>

            <section id="report-blacklist" className="scroll-mt-24">
              <Card title="Blacklist Report" subtitle="DNS blacklist checks for domain and sending IP">
                <Table headers={["Date", "Domain", "Domain Blacklist Count", "Sending IP", "IP Blacklist Count", "Details"]}>
                  {bundle.blacklist.map((b) => (
                    <tr key={b.id} className="table-row-hover">
                      <td className="px-4 py-3 text-ink-500">{fmtDateTime(b.checkedAt)}</td>
                      <td className="px-4 py-3 font-medium text-ink-800">{b.domain || "—"}</td>
                      <td className="px-4 py-3"><Badge tone={b.domainBlacklisted ? "danger" : b.domainCount === 0 && b.domain ? "success" : "neutral"}>{b.domainCount}</Badge></td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-700">{b.ip ?? "—"}</td>
                      <td className="px-4 py-3"><Badge tone={b.ipBlacklisted ? "danger" : "neutral"}>{b.ipCount}</Badge></td>
                      <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => setBlacklistDetail(b)}>Show Details</Button></td>
                    </tr>
                  ))}
                </Table>
                {!bundle.blacklist.length && <p className="py-6 text-center text-sm text-ink-400">No blacklist checks for this run yet.</p>}
              </Card>
            </section>

            <section id="report-placement" className="scroll-mt-24">
              <Card title="Email Placement" subtitle="Every message, its exact mailbox folder, and authentication status">
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <Select value={placementFilters.provider} onChange={(e) => setPlacementFilters({ ...placementFilters, provider: e.target.value })} className="w-44">
                    <option value="">All providers</option>
                    {[...new Set(placementData?.rows.map((r) => r.provider) ?? [])].map((p) => <option key={p} value={p}>{PROVIDER_LABELS[p] ?? p}</option>)}
                  </Select>
                  <Select value={placementFilters.placement} onChange={(e) => setPlacementFilters({ ...placementFilters, placement: e.target.value })} className="w-44">
                    <option value="">All placements</option>
                    {Object.entries(PLACEMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </Select>
                  <Select value={placementFilters.auth} onChange={(e) => setPlacementFilters({ ...placementFilters, auth: e.target.value })} className="w-44">
                    <option value="">Any authentication</option>
                    <option value="pass">All pass</option>
                    <option value="fail">Any fail</option>
                  </Select>
                  <Button variant="secondary" size="sm" onClick={async () => {
                    const blob = await apiBlob(`/reports/placements/${runId}/export`);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `placements-${runId}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}>
                    Export CSV
                  </Button>
                </div>
                {placementsLoading ? (
                  <Spinner label="Loading placements…" />
                ) : (
                  <Table headers={["Recipient", "Provider", "Sender Email", "Delivered To", "Folder", "SMTP Blacklisted", "SPF", "DKIM", "DMARC", ""]}>
                    {placementData?.rows.map((r) => (
                      <tr key={r.id} className="table-row-hover">
                        <td className="px-4 py-3 text-ink-800">{r.recipient}</td>
                        <td className="px-4 py-3 text-ink-500">{PROVIDER_LABELS[r.provider] ?? r.provider}</td>
                        <td className="px-4 py-3 text-ink-600">{r.senderEmail}</td>
                        <td className="px-4 py-3">
                          <Badge tone={statusTone(r.deliveredTo)}>{PLACEMENT_LABELS[r.deliveredTo] ?? r.deliveredTo}</Badge>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-ink-500">{r.folder ?? "—"}</td>
                        <td className="px-4 py-3">{r.smtpBlacklisted ? <Badge tone="danger">Yes</Badge> : <Badge tone="success">No</Badge>}</td>
                        <td className="px-4 py-3"><AuthBadge value={r.spf} /></td>
                        <td className="px-4 py-3"><AuthBadge value={r.dkim} /></td>
                        <td className="px-4 py-3"><AuthBadge value={r.dmarc} /></td>
                        <td className="px-4 py-3"><Button variant="ghost" size="sm" onClick={() => setDetail(r)}>Details</Button></td>
                      </tr>
                    ))}
                  </Table>
                )}
              </Card>
            </section>

            <section id="report-technical" className="scroll-mt-24">
              <Card title="Technical Details" subtitle="Headers and received chain per message">
                <Table headers={["Recipient / Folder", "Message-ID", "From", "Subject", "Return-Path", "Sender IP", "Actions"]}>
                  {bundle.technical.map((t) => (
                    <tr key={t.id} className="table-row-hover align-top">
                      <td className="max-w-[140px] truncate px-4 py-3 font-mono text-xs text-ink-600">{t.to ?? "—"}</td>
                      <td className="max-w-[220px] truncate px-4 py-3 font-mono text-xs text-ink-500">{t.messageId ?? "—"}</td>
                      <td className="max-w-[180px] truncate px-4 py-3 text-xs text-ink-600">{t.from ?? "—"}</td>
                      <td className="max-w-[180px] truncate px-4 py-3 text-xs text-ink-600">{t.subject ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">{t.returnPath ?? "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">{t.senderIp ?? "—"}</td>
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" onClick={() => window.open(`data:text/plain;charset=utf-8,${encodeURIComponent(t.rawHeaders ?? "")}`)}>View Raw Headers</Button>
                      </td>
                    </tr>
                  ))}
                </Table>
                {!bundle.technical.length && <p className="py-6 text-center text-sm text-ink-400">No headers captured for this run.</p>}
              </Card>
            </section>
          </div>
        </div>
      )}

      <Modal open={Boolean(blacklistDetail)} onClose={() => setBlacklistDetail(null)} title="Blacklist Details">
        {blacklistDetail && (
          <Table headers={["Blacklist", "Status", "Response", "Checked At"]}>
            {blacklistDetail.rows.map((r, i) => (
              <tr key={i} className="table-row-hover">
                <td className="px-4 py-2.5 text-sm text-ink-700">{r.list}</td>
                <td className="px-4 py-2.5"><Badge tone={statusTone(r.status)}>{r.status.replace("_", " ")}</Badge></td>
                <td className="px-4 py-2.5 font-mono text-xs text-ink-500">{r.response ?? "—"}</td>
                <td className="px-4 py-2.5 text-xs text-ink-500">{fmtDateTime(r.checkedAt)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Modal>

      <Modal open={showSpamDetails} onClose={() => setShowSpamDetails(false)} title="Spam Filter Details" wide>
        {bundle.spam && (
          <div className="space-y-4">
            <p className="text-sm text-ink-600">
              Score <strong>{bundle.spam.score}</strong> out of threshold <strong>{bundle.spam.threshold}</strong> —{" "}
              <Badge tone={bundle.spam.status === "spam" ? "danger" : "success"}>{bundle.spam.status === "spam" ? "Spam" : "Not spam"}</Badge>
            </p>
            <Table headers={["Rule", "Score", "Description"]}>
              {bundle.spam.rules.map((r) => (
                <tr key={r.rule}>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.rule}</td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.score}</td>
                  <td className="px-4 py-2.5 text-xs">{r.description}</td>
                </tr>
              ))}
            </Table>
            <p className="text-xs text-ink-400">Source: {bundle.spam.source === "spamassassin" ? "SpamAssassin" : "Built-in heuristic analyzer"}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-ink-500">{k}</dt>
      <dd className="font-medium text-ink-800">{v}</dd>
    </div>
  );
}

function SummaryCard({ label, count, pct, tone }: { label: string; count: number; pct: number; tone: "success" | "warning" | "danger" | "neutral" }) {
  const colors = { success: "text-emerald-600", warning: "text-amber-600", danger: "text-red-600", neutral: "text-ink-800" };
  return (
    <div className="rounded-xl border border-ink-200 bg-white p-5 shadow-card">
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>
      <p className={cls("mt-2 text-3xl font-bold tabular-nums", colors[tone])}>{count}</p>
      <p className="mt-1 text-sm text-ink-500">{fmtPct(pct)}</p>
    </div>
  );
}

function CompareCard({ label, inbox, spam, score, highlight }: { label: string; inbox: number | null | undefined; spam: number | null | undefined; score: number | null | undefined; highlight?: boolean }) {
  return (
    <div className={cls("rounded-xl border p-4", highlight ? "border-brand-300 bg-brand-50/40" : "border-ink-200 bg-white")}>
      <p className="text-xs font-medium text-ink-500">{label}</p>
      <div className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-ink-500">Inbox</span><span className="font-semibold text-emerald-600">{fmtPct(inbox)}</span></div>
        <div className="flex justify-between"><span className="text-ink-500">Spam</span><span className="font-semibold text-red-600">{fmtPct(spam)}</span></div>
        <div className="flex justify-between"><span className="text-ink-500">Score</span><span className="font-semibold text-ink-800">{score ?? "—"}</span></div>
      </div>
    </div>
  );
}

function Delta({ label, value, invert }: { label: string; value: number; invert?: boolean }) {
  const positive = invert ? value < 0 : value > 0;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-ink-500">{label}</span>
      <span className={cls("font-semibold", positive ? "text-emerald-600" : "text-red-600")}>
        {value > 0 ? "+" : ""}{value}%
      </span>
    </div>
  );
}

function ScoreCard({ title, score }: { title: string; score: { score: number; status: string; explanation: string; recommendations: string[] } }) {
  return (
    <Card title={title}>
      <div className="flex items-center gap-3">
        <span className="text-3xl font-bold text-ink-900">{score.score}<span className="text-base text-ink-400">/100</span></span>
        <Badge tone={score.score >= 70 ? "success" : score.score >= 45 ? "warning" : "danger"}>{score.status}</Badge>
      </div>
      <p className="mt-2 text-xs text-ink-500">{score.explanation}</p>
      {score.recommendations.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-ink-600">
          {score.recommendations.map((r, i) => <li key={i}>• {r}</li>)}
        </ul>
      )}
    </Card>
  );
}

function ContentLine({ k, v }: { k: string; v: number | string }) {
  return (
    <div className="flex justify-between border-b border-ink-50 pb-1.5">
      <span className="text-ink-500">{k}</span>
      <span className="font-medium text-ink-800">{v}</span>
    </div>
  );
}

function Infra({ k, v }: { k: string; v: string }) {
  return (
    <div className="rounded-lg border border-ink-100 bg-ink-50/40 p-3">
      <div className="text-[11px] font-medium uppercase tracking-wide text-ink-400">{k}</div>
      <div className="mt-1 break-words text-sm font-medium text-ink-800">{v}</div>
    </div>
  );
}

function AuthBadge({ value }: { value: string }) {
  return <Badge tone={statusTone(value)}>{value.toUpperCase()}</Badge>;
}
